import {
  getTaskEstimatedDurationMs,
  getTaskRemainingDurationMs,
} from "./taskEstimatedDurations";

const CREATIVE_JOB_PROGRESS_STORAGE_KEY_V1 = "creativeJobProgress:v1";
const CREATIVE_JOB_PROGRESS_STORAGE_KEY = "creativeJobProgress:v2";
const MAX_STORED_PROGRESS = 99;

const readStorageMap = (storageKey) => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return {};

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeStorageMap = (storageKey, progressMap) => {
  try {
    if (!progressMap || Object.keys(progressMap).length === 0) {
      localStorage.removeItem(storageKey);
      return;
    }

    localStorage.setItem(storageKey, JSON.stringify(progressMap));
  } catch {
    // localStorage may be unavailable in private browsing or when quota is exceeded
  }
};

const readV1ProgressMap = () =>
  readStorageMap(CREATIVE_JOB_PROGRESS_STORAGE_KEY_V1);

const readProgressMap = () => readStorageMap(CREATIVE_JOB_PROGRESS_STORAGE_KEY);

const writeProgressMap = (progressMap) =>
  writeStorageMap(CREATIVE_JOB_PROGRESS_STORAGE_KEY, progressMap);

const clampProgress = (value) =>
  Math.min(Math.max(Number(value) || 0, 0), MAX_STORED_PROGRESS);

/**
 * Builds a v2 entry from a legacy numeric progress value.
 * Only called when promoting/persisting — not on every read of a complete v2 entry.
 */
const entryFromLegacyProgress = (progressValue, estimatedDurationMs, now) => {
  const progress = clampProgress(progressValue);
  const duration = Number(estimatedDurationMs) || 0;

  if (!duration || progress <= 0) {
    return {
      progress,
      startedAt: now,
      estimatedDurationMs: duration,
    };
  }

  const elapsedMs = (progress / 100) * duration;
  return {
    progress,
    startedAt: now - elapsedMs,
    estimatedDurationMs: duration,
  };
};

const normalizeV2Entry = (value, estimatedDurationMs) => {
  const progress = clampProgress(value?.progress);
  const duration =
    Number(value?.estimatedDurationMs) || Number(estimatedDurationMs) || 0;
  const startedAt = Number(value?.startedAt) || 0;

  return {
    progress,
    startedAt,
    estimatedDurationMs: duration,
  };
};

const removeKeyFromMap = (storageKey, jobID) => {
  const progressMap = readStorageMap(storageKey);
  if (!(jobID in progressMap)) return;

  const remaining = { ...progressMap };
  delete remaining[jobID];
  writeStorageMap(storageKey, remaining);
};

/**
 * Reads a session entry, promoting v1 → v2 once when needed.
 * Does not invent startedAt for missing keys (returns null).
 */
const getOrPromoteEntry = (jobID, estimatedDurationMs) => {
  if (!jobID) return null;

  const progressMap = readProgressMap();
  const v2Entry = progressMap[jobID];

  if (v2Entry !== undefined) {
    if (typeof v2Entry === "number") {
      const promoted = entryFromLegacyProgress(
        v2Entry,
        estimatedDurationMs,
        Date.now()
      );
      writeProgressMap({ ...progressMap, [jobID]: promoted });
      return promoted;
    }

    return normalizeV2Entry(v2Entry, estimatedDurationMs);
  }

  const v1Map = readV1ProgressMap();
  const v1Progress = v1Map[jobID];

  if (v1Progress === undefined) {
    return null;
  }

  const promoted = entryFromLegacyProgress(
    v1Progress,
    estimatedDurationMs,
    Date.now()
  );
  writeProgressMap({ ...progressMap, [jobID]: promoted });
  removeKeyFromMap(CREATIVE_JOB_PROGRESS_STORAGE_KEY_V1, jobID);

  return promoted;
};

const resolveDuration = ({ jobType, estimatedDurationMs }) =>
  estimatedDurationMs ?? getTaskEstimatedDurationMs({ jobType });

const buildProgressState = (entry, duration, now = Date.now()) => {
  if (!entry) {
    return {
      progress: 0,
      startedAt: null,
      estimatedDurationMs: duration,
      remainingMs: duration,
    };
  }

  const estimatedDurationMs = entry.estimatedDurationMs || duration;
  const remainingMs = getTaskRemainingDurationMs({
    startedAt: entry.startedAt,
    estimatedDurationMs,
    now,
  });

  let progress = clampProgress(entry.progress);

  if (entry.startedAt && estimatedDurationMs > 0) {
    const elapsedProgress =
      ((now - entry.startedAt) / estimatedDurationMs) * 100;
    progress = clampProgress(Math.max(progress, elapsedProgress));
  }

  return {
    progress,
    startedAt: entry.startedAt || null,
    estimatedDurationMs,
    remainingMs,
  };
};

export const getCreativeJobProgress = (jobID) => {
  if (!jobID) return 0;

  const entry = getOrPromoteEntry(jobID, 0);
  return entry ? clampProgress(entry.progress) : 0;
};

export const getCreativeJobProgressState = ({
  jobID,
  jobType,
  estimatedDurationMs,
  now = Date.now(),
} = {}) => {
  const duration = resolveDuration({ jobType, estimatedDurationMs });
  const entry = getOrPromoteEntry(jobID, duration);
  return buildProgressState(entry, duration, now);
};

export const ensureCreativeJobProgressSession = ({
  jobID,
  jobType,
  estimatedDurationMs,
} = {}) => {
  if (!jobID) return;

  const duration = resolveDuration({ jobType, estimatedDurationMs });
  const existing = getOrPromoteEntry(jobID, duration);
  if (existing) {
    // Backfill duration if a promoted/legacy entry lacked it.
    if (!existing.estimatedDurationMs && duration) {
      writeProgressMap({
        ...readProgressMap(),
        [jobID]: { ...existing, estimatedDurationMs: duration },
      });
    }
    return;
  }

  writeProgressMap({
    ...readProgressMap(),
    [jobID]: {
      progress: 0,
      startedAt: Date.now(),
      estimatedDurationMs: duration,
    },
  });
};

export const setCreativeJobProgress = ({
  jobID,
  progress,
  jobType,
  estimatedDurationMs,
}) => {
  if (!jobID) return;

  const normalizedProgress = Number(progress);
  if (!Number.isFinite(normalizedProgress) || normalizedProgress < 0) return;

  const duration = resolveDuration({ jobType, estimatedDurationMs });
  const currentEntry = getOrPromoteEntry(jobID, duration);
  const nextProgress = clampProgress(
    Math.max(currentEntry?.progress || 0, normalizedProgress)
  );

  if (
    currentEntry &&
    nextProgress <= currentEntry.progress &&
    currentEntry.startedAt &&
    currentEntry.estimatedDurationMs
  ) {
    return;
  }

  const startedAt = currentEntry?.startedAt || Date.now();

  writeProgressMap({
    ...readProgressMap(),
    [jobID]: {
      progress: nextProgress,
      startedAt,
      estimatedDurationMs: currentEntry?.estimatedDurationMs || duration,
    },
  });
};

export const clearCreativeJobProgress = (jobID) => {
  if (!jobID) return;

  removeKeyFromMap(CREATIVE_JOB_PROGRESS_STORAGE_KEY, jobID);
  removeKeyFromMap(CREATIVE_JOB_PROGRESS_STORAGE_KEY_V1, jobID);
};

/**
 * Moves stored progress from a temporary alert id to the RTDB jobID
 * so reload can restore the same percentage and countdown.
 */
export const migrateCreativeJobProgress = ({
  fromKey,
  toKey,
  jobType,
  estimatedDurationMs,
}) => {
  if (!fromKey || !toKey || fromKey === toKey) return;

  const duration = resolveDuration({ jobType, estimatedDurationMs });
  const fromEntry = getOrPromoteEntry(fromKey, duration);

  if (!fromEntry) return;
  if (fromEntry.progress <= 0 && !fromEntry.startedAt) return;

  const nextEntry = {
    ...fromEntry,
    estimatedDurationMs: fromEntry.estimatedDurationMs || duration,
  };

  const progressMap = readProgressMap();
  const remaining = { ...progressMap, [toKey]: nextEntry };
  delete remaining[fromKey];
  writeProgressMap(remaining);

  removeKeyFromMap(CREATIVE_JOB_PROGRESS_STORAGE_KEY_V1, fromKey);
  removeKeyFromMap(CREATIVE_JOB_PROGRESS_STORAGE_KEY_V1, toKey);
};
